import { FC, Fragment } from 'react';
import { IconButton, Link, Typography } from '@mui/material';
import { Event as EventIcon, ContentCopy as ContentCopyIcon, Mood as MoodIcon } from '@mui/icons-material';
import moment from 'moment-timezone';

import { useInfoMessage } from '../../../../../hooks/useInfoMessage';
import Column from '../../../../../components/Column';
import OverviewList, { OverviewListItem } from '../../../../../components/List/OverviewList';
import Row from '../../../../../components/Row';

import { Campaign } from '../../../../../types/Campaign';

interface CampaignModificationOverviewListProps {
    campaign: Campaign;
}

const CampaignModificationOverviewList: FC<CampaignModificationOverviewListProps> = ({ campaign }) => {
    const { showInfoMessage } = useInfoMessage();

    const items: OverviewListItem[] = [
        {
            icon: MoodIcon,
            primary: 'Created By',
            secondary: (
                <Column gap={0} alignItems="flex-end">
                    {campaign?.dspCampaignId ? (
                        <Fragment>
                            {campaign?.createdByUser?.name ? (
                                <Link href={`/users/${campaign?.createdByUser?.id}`} target="_blank" fontWeight={500}>
                                    {campaign?.createdByUser?.name}
                                </Link>
                            ) : (
                                ' - '
                            )}

                            {campaign?.createdByUser?.emailAddress && (
                                <Row gap={0} alignItems="center">
                                    <Typography variant="caption" color="text.secondary">
                                        {campaign?.createdByUser?.emailAddress}
                                    </Typography>
                                    <IconButton
                                        aria-label="Copy Created By Email"
                                        edge="end"
                                        disableRipple
                                        title="Copy Created By Email"
                                        sx={{ fontSize: 16, py: 0 }}
                                        onClick={(): void => {
                                            navigator.clipboard.writeText(
                                                campaign?.createdByUser?.emailAddress as string
                                            );

                                            showInfoMessage('success', 'Copied Created By Email.');
                                        }}
                                    >
                                        <ContentCopyIcon fontSize="inherit" />
                                    </IconButton>
                                </Row>
                            )}
                        </Fragment>
                    ) : (
                        ' - '
                    )}
                </Column>
            ),
        },
        {
            icon: EventIcon,
            primary: 'Created On',
            secondary: (
                <Column gap={0} alignItems="flex-end">
                    {campaign?.createdDate ? (
                        <Fragment>
                            <Typography variant="body2" color="text.primary" fontWeight={500}>
                                {moment(campaign?.createdDate).format('MMMM D, YYYY')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {moment(campaign?.createdDate).format('hh:mm A')}
                            </Typography>
                        </Fragment>
                    ) : (
                        ' - '
                    )}
                </Column>
            ),
        },
        {
            icon: MoodIcon,
            primary: 'Updated By',
            secondary: (
                <Column gap={0} alignItems="flex-end">
                    {campaign?.dspCampaignId ? (
                        <Fragment>
                            {campaign?.modifiedByUser?.name ? (
                                <Link href={`/users/${campaign?.modifiedByUser?.id}`} target="_blank" fontWeight={500}>
                                    {campaign?.modifiedByUser?.name}
                                </Link>
                            ) : (
                                ' - '
                            )}

                            {campaign?.modifiedByUser?.emailAddress && (
                                <Row gap={0} alignItems="center">
                                    <Typography variant="caption" color="text.secondary">
                                        {campaign?.modifiedByUser?.emailAddress}
                                    </Typography>
                                    <IconButton
                                        aria-label="Copy Updated By Email"
                                        edge="end"
                                        disableRipple
                                        title="Copy Updated By Email"
                                        sx={{ fontSize: 16, py: 0 }}
                                        onClick={(): void => {
                                            navigator.clipboard.writeText(
                                                campaign?.modifiedByUser?.emailAddress as string
                                            );

                                            showInfoMessage('success', 'Copied Updated By Email.');
                                        }}
                                    >
                                        <ContentCopyIcon fontSize="inherit" />
                                    </IconButton>
                                </Row>
                            )}
                        </Fragment>
                    ) : (
                        ' - '
                    )}
                </Column>
            ),
        },
        {
            icon: EventIcon,
            primary: 'Updated On',
            secondary: (
                <Column gap={0} alignItems="flex-end">
                    {campaign?.modifiedDate ? (
                        <Fragment>
                            <Typography variant="body2" color="text.primary" fontWeight={500}>
                                {moment(campaign?.modifiedDate).format('MMMM D, YYYY')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {moment(campaign?.modifiedDate).format('hh:mm A')}
                            </Typography>
                        </Fragment>
                    ) : (
                        ' - '
                    )}
                </Column>
            ),
        },
    ];

    return <OverviewList items={items} sx={{ p: 0 }} />;
};

export type { CampaignModificationOverviewListProps };
export default CampaignModificationOverviewList;
